import {createSelector} from 'reselect'

import {isPresent, isNumeric} from '../../../common/utils.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import apiverson from '../../../common/apiverson.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {setReturnOrder} from '../../../redux/actions/data/returnOrders.js'
import {
  setForm,
  updateForm,
  removeForm,
  onlyIfForm,
  useSelector,
  getState,
  dispatch,
  updateFormObject,
} from '../../../store.js'
import {returnOrderSelector} from '../../../redux/selectors/data/returnOrders.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'

import {refreshReturnOrderList} from '../returnOrderListActions.js'
import {usesInventorySelector} from '../../../data/company.js'
import {getProductName, productSelector} from '../../../data/products.js'
import {nonFBAWarehouseOptionsSelector} from '../../../data/warehouses.js'
import NumberInput from '../../../common/components/Form/NumberInput.js'
import Select from '../../../common/components/Select.js'
import Icon from '../../../common/icons/Icon.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'

export const MODAL_FORM = 'receive_return_order_modal'

export function showReceiveReturnOrderModal(
  referenceID,
  lineIDs,
  focusType = 'received',
) {
  const returnOrder = returnOrderSelector(getState(), {referenceID})

  const lines = returnOrder.lines.reduce((prev, line) => {
    if (lineIDs.includes(line.line_id)) {
      prev.push({
        ...line,
        expectedQuantity: `${line.expected_quantity || 0}`,
        toBeReceivedQuantity: '0',
        toBeRestockedQuantity: '0',
        restockWarehouseID: line.restock_warehouse_id || null,
      })
    }

    return prev
  }, [])

  setForm(MODAL_FORM, {
    referenceID,
    lines,
    focusType,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function updateLine(index, updates) {
  updateFormObject(MODAL_FORM, ['lines', index], updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export const errorsSelector = createSelector(modalFormSelector, (form) => {
  const errors = {}

  if (!form) {
    return errors
  }

  form.lines.forEach(
    (
      {
        expectedQuantity,
        toBeReceivedQuantity,
        toBeRestockedQuantity,
        restockWarehouseID,
      },
      index,
    ) => {
      if (expectedQuantity && !isNumeric(expectedQuantity)) {
        errors[`lines__${index}__expectedQuantity`] =
          'Quantity must be a number'
        errors.preventSave = true
      }

      if (toBeReceivedQuantity && !isNumeric(toBeReceivedQuantity)) {
        errors[`lines__${index}__toBeReceivedQuantity`] =
          'Quantity must be a number'
        errors.preventSave = true
      }

      if (toBeRestockedQuantity && !isNumeric(toBeRestockedQuantity)) {
        errors[`lines__${index}__toBeRestockedQuantity`] =
          'Quantity must be a number'
        errors.preventSave = true
      }

      if (
        isNumeric(toBeRestockedQuantity) &&
        Number(toBeRestockedQuantity) !== 0 &&
        !isPresent(restockWarehouseID)
      ) {
        errors[`lines__${index}__restockWarehouseID`] =
          'A restock warehouse is required'
        errors.preventSave = true
      }
    },
  )

  return errors
})

export function forceReceiveLine(index) {
  const {lines} = modalFormSelector(getState())
  const line = lines[index]

  updateLine(index, {toBeReceivedQuantity: line.expectedQuantity})
}

export function forceRestockLine(index) {
  const {lines} = modalFormSelector(getState())
  const line = lines[index]

  updateLine(index, {toBeRestockedQuantity: line.expectedQuantity})
}

export async function updateReturnOrderLine(
  referenceID,
  {
    expectedQuantity,
    toBeReceivedQuantity,
    toBeRestockedQuantity,
    restockWarehouseID,
    ...line
  },
  stats,
) {
  const params = {}

  expectedQuantity = Number(expectedQuantity) || 0
  toBeReceivedQuantity = Number(toBeReceivedQuantity) || 0
  toBeRestockedQuantity = Number(toBeRestockedQuantity) || 0

  if (expectedQuantity !== line.expected_quantity) {
    params.expected_quantity = expectedQuantity
  }

  if (toBeReceivedQuantity !== 0) {
    params.received_quantity = line.received_quantity + toBeReceivedQuantity
  }

  if (toBeRestockedQuantity !== 0) {
    params.restocked_quantity = line.restocked_quantity + toBeRestockedQuantity
  }

  if (restockWarehouseID !== line.restock_warehouse_id) {
    params.restock_warehouse_id = restockWarehouseID
  }

  if (Object.keys(params).length === 0) {
    return
  }

  const {json} = await apiverson.put(
    `/return_order/${encodeURIComponent(referenceID)}/line/${line.line_id}`,
    params,
  )

  dispatch(setReturnOrder(json))

  stats.count = stats.count + 1
}

export async function receiveReturnOrder() {
  try {
    updateModalForm({isSaving: true})

    const {referenceID, lines} = modalFormSelector(getState())
    const stats = {count: 0}

    await Promise.all(
      lines.map((line) => updateReturnOrderLine(referenceID, line, stats)),
    )

    if (stats.count !== 0) {
      dispatch(refreshReturnOrderList())
    }

    showMessageToast(
      `${stats.count} RMA Line${stats.count !== 1 ? 's were' : ' was'} updated`,
    )

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: `Error setting RMA status: ${
        err.message || err.error_message
      }`,
    })
  } finally {
    updateModalForm({isSaving: false})
  }
}

function Line({
  index,
  line: {
    expectedQuantity,
    toBeReceivedQuantity,
    toBeRestockedQuantity,
    restockWarehouseID,
    ...line
  },
  errors,
  autoFocusExpected,
  autoFocusReceived,
  autoFocusRestocked,
}) {
  const name = getProductName(
    useSelector((state) => productSelector(state, {sku: line.sku})),
  )
  const warehouseOptions = useSelector(nonFBAWarehouseOptionsSelector)
  const usesInventory = useSelector(usesInventorySelector)

  return (
    <tr>
      <td className="table__td">
        <div className="fs-n1">
          <strong>{name}</strong>
        </div>
        <div className="fs-n1">{line.sku}</div>
      </td>
      <td className="table__td">
        <span
          title={line.code ? line.code.description : 'None specified'}
          className="fs-n1"
        >
          {line.code ? line.code.code : <em>None specified</em>}
        </span>
      </td>
      <td className="table__td border-left--light align-center">
        <div className="inline-block">
          <NumberInput
            className="margin-bottom-0"
            value={expectedQuantity}
            onChange={(value) =>
              updateLine(index, {expectedQuantity: `${value}`})
            }
            autoFocus={autoFocusExpected}
            errorMessage={errors[`lines__${index}__expectedQuantity`]}
          />
        </div>
      </td>
      <td className="table__td table__td--verified overflow-hidden relative border-left--light align-center">
        <div className="inline-block">
          <NumberInput
            className="margin-bottom-0"
            value={toBeReceivedQuantity}
            onChange={(value) =>
              updateLine(index, {toBeReceivedQuantity: `${value}`})
            }
            autoFocus={autoFocusReceived}
            errorMessage={errors[`lines__${index}__toBeReceivedQuantity`]}
          />
        </div>
        <div className="fs-n2 text--lt-grey margin-top-5">
          (Previous: <span>{line.received_quantity}</span>)
        </div>
        <ButtonLink
          className="btn--force-verify lighter no-underline"
          tabIndex="-1"
          title="Force-verify that this line has been completely received"
          onClick={() => forceReceiveLine(index)}
        >
          <Icon name="check-circle" className="fs-01 lh-0" />
        </ButtonLink>
      </td>
      {usesInventory && (
        <td className="table__td table__td--verified overflow-hidden relative border-left--light align-center">
          <div className="inline-block">
            <NumberInput
              className="margin-bottom-0"
              value={toBeRestockedQuantity}
              onChange={(value) =>
                updateLine(index, {toBeRestockedQuantity: `${value}`})
              }
              autoFocus={autoFocusRestocked}
              disabled={!restockWarehouseID}
              errorMessage={errors[`lines__${index}__toBeRestockedQuantity`]}
            />
          </div>
          <div className="fs-n2 text--lt-grey margin-top-5">
            (Previous: <span>{line.restocked_quantity}</span>)
          </div>
          <ButtonLink
            className="btn--force-verify lighter no-underline"
            tabIndex="-1"
            title="Force-verify that this line has been completely restocked"
            onClick={() => forceRestockLine(index)}
            disabled={!restockWarehouseID}
          >
            <Icon name="check-circle" className="fs-01 lh-0" />
          </ButtonLink>
        </td>
      )}
      <td className="table__td border-left--light">
        <Select
          className="w-100"
          value={restockWarehouseID || ''}
          onChange={(value) => {
            value = value ? Number(value) : null

            updateLine(index, {restockWarehouseID: value})

            if (!value) {
              updateLine(index, {toBeRestockedQuantity: '0'})
            }
          }}
          disabled={line.restocked_quantity !== 0}
          errorMessage={errors[`lines__${index}__restockWarehouseID`]}
        >
          <option value="">None specified</option>
          {warehouseOptions.map(({value, display}) => (
            <option key={value} value={value}>
              {display}
            </option>
          ))}
        </Select>
      </td>
    </tr>
  )
}

function ReceiveReturnOrderModal({form}) {
  const usesInventory = useSelector(usesInventorySelector)
  const errors = useSelector(errorsSelector)

  return (
    <ConfirmModal
      title="Receive/Restock Returned Items"
      modalSize="x-lg"
      onConfirm={() => receiveReturnOrder()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
    >
      <>
        <p className="fs-01 margin-bottom-20">
          <strong>Items in RMA</strong>
        </p>
        <table className="table">
          <thead>
            <tr>
              <th className="table__th table__th--sm w-15">Product Name/SKU</th>
              <th className="table__th table__th--sm w-10">Return Code</th>
              <th className="table__th table__th--sm w-10 border-left--light align-center">
                Qty Expected
              </th>
              <th className="table__th table__th--sm w-10 border-left--light align-center">
                Qty to Receive
              </th>
              {usesInventory && (
                <th className="table__th table__th--sm w-10 border-left--light align-center">
                  Qty to Restock
                </th>
              )}
              <th className="table__th table__th--sm w-20 border-left--light">
                Restock Location
              </th>
            </tr>
          </thead>
          <tbody className="table__tbody--lines">
            {form.lines.map((line, index) => (
              <Line
                key={line.line_id}
                index={index}
                line={line}
                errors={errors}
                autoFocusExpected={index === 0 && form.focusType === 'expected'}
                autoFocusReceived={index === 0 && form.focusType === 'received'}
                autoFocusRestocked={
                  index === 0 && form.focusType === 'restocked'
                }
              />
            ))}
          </tbody>
        </table>
      </>
    </ConfirmModal>
  )
}

export default onlyIfForm(ReceiveReturnOrderModal, modalFormSelector)
